import { ClearOutlined, FilterOutlined, PlusOutlined } from '@ant-design/icons'
import { Button, notification, Spin, Tooltip } from 'antd'
import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'

import { QUERY_KEYS } from '@api'
import { DeleteTicketModal } from '@components'
import { useFilters, useGeneral, useUser } from '@contexts'
import {
  useCarriers,
  useConfigurationProperties,
  useCreateTicket,
  useDeleteTicket,
  useEquipment,
  useGetTickets,
  useMaterials,
  useSuppliers,
  useSuppliersZones,
  useTickets,
} from '@hooks'

import RangeTimeSwitcher from '../../../../components/RangeTimeSwitcher'
import {
  ContentContainer,
  StyledButton,
  StyledRow,
  StyledSelect,
} from '../../../../components/StyledComponents'
import { TicketModal } from '../../../../components/TicketModal'
import { useHandleDeleteTicketModal } from '../../../../contexts/HandleDeleteTicketModalContext'
import { useHandleTicketModal } from '../../../../contexts/HandleTicketModalContext'
import {
  DayToPushProps,
  Filters,
  FormTypeSupplier,
  RoleEnum,
  StatusTicketEnum,
  Ticket,
} from '../../../../types'
import { useApproveTicket } from '../../AdminDashboard/hooks'
import RequestsBoard from '../components/RequestsBoard'

dayjs.extend(isoWeek)

const { ADMIN } = RoleEnum

const Ongoing = () => {
  const { t } = useTranslation()
  const { roles } = useUser()
  const queryClient = useQueryClient()
  const { filters, setFilters, resetFilters } = useFilters()
  const { changeTicket } = useTickets()
  const { tickets, ticketsIsLoading } = useGetTickets(filters)
  const { suppliersOptions, isLoading: isSuppliersLoading, suppliers } = useSuppliers()
  const { suppliersZones, isLoading: isSuppliersZonesLoading } = useSuppliersZones()
  const { equipmentOptions, isLoading: isEquipmentLoading } = useEquipment()
  const { createTicket, isLoading: isCreateTicketLoading } = useCreateTicket()
  const { ticketToEdit, setTicketToEdit, setIsTicketModalOpen } = useHandleTicketModal()
  const { approveTicket } = useApproveTicket()
  const { allCarriersOptions, isLoadingAllCarriers } = useCarriers()
  const {
    setTicketToSearch,
    ticketToSearch,
    filtersDisplayed,
    handleDisplayFilters,
    menuDisplayed,
  } = useGeneral()
  const { isDeleteTicketModalOpen, setIsDeleteTicketModalOpen } = useHandleDeleteTicketModal()
  const { isLoading: isDeleteTicketLoading } = useDeleteTicket()
  const { assignmentTicketsData } = useConfigurationProperties()
  const { materialsOptions } = useMaterials()

  useEffect(() => {
    if (ticketToSearch && tickets?.total === 1) {
      const [ticket] = tickets?.tickets
      if (ticket) {
        setFilters((prevState: Filters) => ({
          ...prevState,
          mondayOfWeek: dayjs(ticket.withdrawalDate).isoWeekday(1).toDate(),
        }))
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickets])

  const submitHandler = (
    id: number | undefined,
    form: FormTypeSupplier,
    resetForm: () => void,
    dates: Date[] | undefined,
    toApproved?: boolean,
  ) => {
    if (id) {
      changeTicket(
        {
          id,
          payload: {
            ...form,
            ticketDate: dates![0],
          },
        },
        {
          onSuccess: () => {
            notification.success({
              message: `${t(
                'admin_router.admin_dashboard.use_approve_tickets.edit_success',
              )} ${id}`,
            })
            if (toApproved) {
              approveTicket({ id, note: form.note, type: form.type })
            }
            resetForm()
          },
        },
      )
    } else {
      createTicket(
        {
          ...form,
          ticketsDate: dates,
        },
        {
          onSuccess: () => {
            resetForm()
          },
        },
      )
    }
  }

  const [ticketsByDay, setTicketsByDay] = useState<DayToPushProps[]>([])

  const daysOfTheWeek = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo']

  const days: string[] = []
  daysOfTheWeek.forEach((day, index) => {
    days.push(dayjs(filters?.mondayOfWeek).add(index, 'days').format('dddd DD-MM'))
  })

  const capitalize = (string: string) => string.charAt(0).toUpperCase() + string.slice(1)

  const filterOptions = [
    {
      label: `${t('admin_router.filter_options.status.created')}`,
      value: StatusTicketEnum.CREATED,
    },
    {
      label: `${t('admin_router.filter_options.status.pending')}`,
      value: StatusTicketEnum.PENDING,
    },
    {
      label: `${t('admin_router.filter_options.status.confirmed')}`,
      value: StatusTicketEnum.CONFIRMED,
    },
    {
      label: `${t('admin_router.filter_options.status.supplier_invoiced')}`,
      value: StatusTicketEnum.TRANSIT,
    },
    {
      label: `${t('tpl_router.filterOptions.delivered')}`,
      value: StatusTicketEnum.DELIVERED,
    },
    {
      label: `${t('globals.generics.status.no_show')}`,
      value: StatusTicketEnum.NO_SHOW,
    },
    {
      label: `${t('admin_router.filter_options.status.canceled')}`,
      value: StatusTicketEnum.CANCELED,
    },
  ]

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleFilterOptions = (input: string, option: any) => {
    if (option.label) return option.label.toString().toLowerCase().includes(input.toLowerCase())
    return true
  }

  const handleStatusFilterChange = (value: any) => {
    setFilters((prevState: Filters) => ({
      ...prevState,
      page: 1,
      statusName: [...(value as string[])],
      ticketId: undefined,
    }))
  }

  const handleSupplierFilterChange = (value: any) => {
    setFilters((prevState: Filters) => ({
      ...prevState,
      page: 1,
      supplierId: [...(value as number[])],
      ticketId: undefined,
      invoiceNumber: undefined,
      taInvoiceNumber: undefined,
    }))
  }

  const handleSupplierZoneFilterChange = (values: any) => {
    const valuesAux = values as string[]
    const suppliersFilters: number[] = []
    valuesAux.forEach(value => {
      const suppliersAux = suppliers?.filter(supplier => supplier.zone === value)
      if (suppliersAux) {
        suppliersAux.forEach(supplierAux => {
          suppliersFilters.push(supplierAux.id)
        })
      }
    })

    setFilters((prevState: Filters) => ({
      ...prevState,
      page: 1,
      supplierId: suppliersFilters,
      ticketId: undefined,
      invoiceNumber: undefined,
      taInvoiceNumber: undefined,
    }))
  }
  const handleEquipmentFilterChange = (value: any) => {
    setFilters((prevState: Filters) => ({
      ...prevState,
      page: 1,
      equipmentId: [...(value as number[])],
      ticketId: undefined,
      invoiceNumber: undefined,
      taInvoiceNumber: undefined,
    }))
  }

  const handleCarrierFilterChange = (value: any) => {
    setFilters((prevState: Filters) => ({
      ...prevState,
      page: 1,
      carrierId: [...(value as number[])],
      ticketId: undefined,
      invoiceNumber: undefined,
      taInvoiceNumber: undefined,
    }))
  }
  const handleMaterialFilterChange = (value: any) => {
    setFilters((prevState: Filters) => ({
      ...prevState,
      page: 1,
      materialId: [...(value as number[])],
      ticketId: undefined,
      invoiceNumber: undefined,
      taInvoiceNumber: undefined,
    }))
  }
  const handleCleanFilters = () => {
    setTicketToSearch(undefined)
    setFilters((prevState: Filters) => ({
      ...prevState,
      page: 1,
      carrierId: [],
      carrierMode: [],
      statusName: [],
      supplierId: [],
      materialId: [],
      equipmentId: [],
      orderBy: undefined,
      ticketId: undefined,
      zone: [],
    }))
    resetFilters()
    queryClient.resetQueries(QUERY_KEYS.TICKETS)
  }

  const handleOpenTicketModal = () => {
    setTicketToEdit(0)
    setIsTicketModalOpen(true)
  }
  const handleCloseTicketModal = () => {
    setIsTicketModalOpen(false)
  }

  const handleCleanSearchInput = () => {
    setTicketToSearch(undefined)
  }

  useEffect(() => {
    if (tickets?.tickets) {
      const ticketsToSet: DayToPushProps[] = []
      days.forEach((day: string) => {
        const dayToPush = {
          date: capitalize(day),
          tickets: tickets?.tickets
            .filter((ticket: Ticket) => dayjs(ticket.withdrawalDate).format('dddd DD-MM') === day)
            .sort((a, b) => {
              const diff =
                new Date(a.withdrawalDate).getTime() - new Date(b.withdrawalDate).getTime()
              if (diff === 0) {
                return a.id - b.id
              }
              return diff
            }),
        }
        ticketsToSet.push(dayToPush)
      })
      setTicketsByDay(ticketsToSet)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickets?.tickets, filters?.mondayOfWeek])

  return (
    <>
      <ContentContainer className="admin-dashboard">
        <StyledRow className="sticky-options">
          {roles?.includes(ADMIN) && (
            <Tooltip placement="bottomRight" title="Nueva solicitud">
              <StyledButton className="inline" onClick={handleOpenTicketModal}>
                <PlusOutlined className="option-button" style={{ fontSize: '18px' }} />
              </StyledButton>
            </Tooltip>
          )}

          <div
            className={`filters-container${
              filtersDisplayed && !menuDisplayed ? ' filters-displayed' : ''
            }`}
          >
            <Button onClick={handleDisplayFilters} id="displayFiltersButton">
              <FilterOutlined />
            </Button>
            <div className="filters-list">
              <Tooltip placement="bottomRight" title={t('globals.generics.clean_filters')}>
                <StyledButton
                  className="inline option"
                  onClick={handleCleanFilters}
                  icon={<ClearOutlined style={{ fontSize: '18px' }} />}
                />
              </Tooltip>
              <StyledSelect
                mode="multiple"
                allowClear
                placeholder={t('admin_router.filter_options.status.title')}
                options={filterOptions}
                onChange={handleStatusFilterChange}
                filterOption={handleFilterOptions}
                value={filters?.statusName}
                onDropdownVisibleChange={handleCleanSearchInput}
                maxTagCount="responsive"
              />
              <StyledSelect
                mode="multiple"
                allowClear
                placeholder={t('admin_router.filter_options.zone')}
                options={suppliersZones}
                onChange={handleSupplierZoneFilterChange}
                filterOption={handleFilterOptions}
                loading={isSuppliersZonesLoading}
                value={filters?.zone}
                onDropdownVisibleChange={handleCleanSearchInput}
                maxTagCount="responsive"
              />
              <StyledSelect
                mode="multiple"
                allowClear
                maxTagCount="responsive"
                placeholder={t('admin_router.filter_options.supplier')}
                options={suppliersOptions}
                filterOption={handleFilterOptions}
                onChange={handleSupplierFilterChange}
                loading={isSuppliersLoading}
                value={filters?.supplierId}
                onDropdownVisibleChange={handleCleanSearchInput}
              />
              <StyledSelect
                mode="multiple"
                allowClear
                placeholder={t('admin_router.filter_options.by_carrier.title')}
                options={allCarriersOptions}
                loading={isLoadingAllCarriers}
                filterOption={handleFilterOptions}
                onChange={handleCarrierFilterChange}
                value={filters?.carrierId}
                onDropdownVisibleChange={handleCleanSearchInput}
              />
              <StyledSelect
                maxTagCount="responsive"
                mode="multiple"
                allowClear
                placeholder={t('suppliers_router.filter_options.material.title')}
                options={materialsOptions}
                filterOption={handleFilterOptions}
                onChange={handleMaterialFilterChange}
                value={filters?.materialId}
                onDropdownVisibleChange={handleCleanSearchInput}
              />
              <StyledSelect
                mode="multiple"
                allowClear
                placeholder={t('admin_router.filter_options.equipment')}
                options={equipmentOptions}
                filterOption={handleFilterOptions}
                onChange={handleEquipmentFilterChange}
                loading={isEquipmentLoading}
                value={filters?.equipmentId}
                onDropdownVisibleChange={handleCleanSearchInput}
              />
            </div>
          </div>
        </StyledRow>
        <StyledRow className="ticket-handler border-bottom">
          <div style={{ flex: 1 }} />
          <RangeTimeSwitcher />
          <div style={{ flex: 1 }} />
        </StyledRow>
        <StyledRow className="boards-container">
          {ticketsIsLoading && (
            <Spin
              style={{
                display: 'flex',
                height: '100%',
                justifyContent: 'center',
                alignItems: 'center',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 2,
                backgroundColor: '#ffffff90',
              }}
            />
          )}
          {ticketsByDay.length > 0 &&
            ticketsByDay.map(day => (
              <RequestsBoard key={day.date} tickets={day.tickets} day={day.date} />
            ))}
        </StyledRow>
      </ContentContainer>
      <TicketModal
        title={t('suppliers_router.new_ticket_modal.title')}
        onClose={callback => {
          handleCloseTicketModal()
          if (callback) callback()
        }}
        onOk={submitHandler}
        isLoading={isCreateTicketLoading}
        autoAssignValue={assignmentTicketsData?.value}
      />
      {isDeleteTicketModalOpen && (
        <DeleteTicketModal
          open={isDeleteTicketModalOpen}
          title={t('suppliers_router.delete_ticket_modal.title')}
          onClose={() => setIsDeleteTicketModalOpen(false)}
          ticketId={ticketToEdit}
          isLoading={isDeleteTicketLoading}
        />
      )}
    </>
  )
}

export { Ongoing }
